/**
 * Offline mutation queue.
 *
 * Queues POST/PUT/DELETE API requests when offline or when fetch fails
 * with a network error. Replays them in FIFO order when connectivity
 * returns.
 *
 * @module offline/queue
 */

import {
  addPendingMutation,
  getAllPendingMutations,
  updatePendingMutation,
  deletePendingMutation,
  getPendingMutationCount,
} from './db';
import type { PendingMutation } from './db';
import { getAuthToken } from './auth-bridge';

// ── Constants ───────────────────────────────────────────────────

const MAX_RETRIES = 5;

// ── Queue state ─────────────────────────────────────────────────

let isFlushing = false;
let flushListeners: Array<() => void> = [];

/**
 * Subscribe to flush completion events.
 * Returns an unsubscribe function.
 */
export function onFlushComplete(listener: () => void): () => void {
  flushListeners.push(listener);
  return () => {
    flushListeners = flushListeners.filter((l) => l !== listener);
  };
}

function notifyFlushComplete(): void {
  for (const listener of flushListeners) {
    listener();
  }
}

// ── Enqueue ─────────────────────────────────────────────────────

export interface EnqueueOptions {
  method: 'POST' | 'PUT' | 'DELETE';
  url: string;
  body?: string;
}

/**
 * Add a mutation to the offline queue.
 * Returns the generated mutation ID.
 */
export async function enqueue(options: EnqueueOptions): Promise<number> {
  const id = await addPendingMutation({
    method: options.method,
    url: options.url,
    body: options.body,
    createdAt: Date.now(),
    retries: 0,
    status: 'pending',
  });
  return id;
}

// ── Flush ───────────────────────────────────────────────────────

/**
 * Replay all pending mutations in FIFO order.
 *
 * - On success: delete from store.
 * - On 4xx: mark as `failed` (client error, don't retry).
 * - On 5xx / network error: increment retries, stop flushing.
 * - Max MAX_RETRIES per mutation; after that, mark `failed`.
 *
 * Returns the number of successfully replayed mutations.
 */
export async function flush(): Promise<number> {
  if (isFlushing) return 0;
  isFlushing = true;

  let successCount = 0;

  try {
    const mutations = await getAllPendingMutations();

    for (const mutation of mutations) {
      if (mutation.status === 'failed') continue;

      // Mark in-flight
      const inFlight: PendingMutation = { ...mutation, status: 'in-flight' };
      await updatePendingMutation(inFlight);

      try {
        // Re-fetch auth token at replay time (never store stale tokens)
        const token = await getAuthToken();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(mutation.url, {
          method: mutation.method,
          headers,
          body: mutation.body,
        });

        if (response.ok || response.status === 204) {
          // Success — remove from queue
          await deletePendingMutation(mutation.id!);
          successCount++;
        } else if (response.status >= 400 && response.status < 500) {
          // Client error — mark failed, don't retry
          await updatePendingMutation({
            ...mutation,
            status: 'failed',
            retries: mutation.retries + 1,
          });
        } else {
          // Server error — retry later
          const newRetries = mutation.retries + 1;
          if (newRetries >= MAX_RETRIES) {
            await updatePendingMutation({
              ...mutation,
              status: 'failed',
              retries: newRetries,
            });
          } else {
            await updatePendingMutation({
              ...mutation,
              status: 'pending',
              retries: newRetries,
            });
            // Stop flushing on server error — try remaining later
            break;
          }
        }
      } catch {
        // Network error — retry later
        const newRetries = mutation.retries + 1;
        if (newRetries >= MAX_RETRIES) {
          await updatePendingMutation({
            ...mutation,
            status: 'failed',
            retries: newRetries,
          });
        } else {
          await updatePendingMutation({
            ...mutation,
            status: 'pending',
            retries: newRetries,
          });
          // Stop flushing on network error
          break;
        }
      }
    }
  } finally {
    isFlushing = false;
    notifyFlushComplete();
  }

  return successCount;
}

// ── Query ───────────────────────────────────────────────────────

/**
 * Get count of pending + in-flight mutations (excludes failed).
 */
export async function getQueueSize(): Promise<number> {
  return getPendingMutationCount();
}

/**
 * Whether flush is currently running.
 */
export function isSyncing(): boolean {
  return isFlushing;
}

// ── Auto-flush on online event ──────────────────────────────────

let autoFlushBound = false;

/**
 * Start listening for `online` events to auto-flush the queue.
 * Safe to call multiple times — only binds once.
 */
export function startAutoFlush(): void {
  if (autoFlushBound) return;
  autoFlushBound = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      void flush();
    });
  }
}

/**
 * Reset internal queue state. Used only in tests.
 */
export function _resetQueueState(): void {
  isFlushing = false;
  flushListeners = [];
  autoFlushBound = false;
}
